import * as React from "react";
import styled from "styled-components";
import { theme } from "styles/theme";

type Props = {
  name?: string;
  value?: string;
  onValueChange: (digits: string) => void;
  disabled?: boolean;
};

const StyledInput = styled.input`
  outline: none;
  font-weight: 500;
  border: 1px solid ${theme.border.light};
  border-radius: 5px;
  background: white;
  margin: 0;
  height: ${theme.height.regular};
  padding: 0 1em;
  transition: all 0.09s linear;
  font-size: ${theme.fontSize.regular};
  font-variant-numeric: tabular-nums;
  &:disabled {
    background: ${theme.bg.blue};
    cursor: not-allowed;
  }
  &:hover {
    border: 1px solid #5d6d7e;
  }
  &:focus {
    background: transparent;
    border: 1px solid #3498db;
  }
`;

const formatDisplay = (digits: string): string => {
  const filled = digits.padStart(4, "0");
  return `${filled.slice(0, 2)}h:${filled.slice(2, 4)}m`;
};

const isValid = (digits: string): boolean => {
  if (digits.length < 2) return true;
  const minutes = parseInt(digits.slice(-2), 10);
  return Number.isFinite(minutes) && minutes <= 59;
};

export default function MaskedDurationInput({
  value = "",
  onValueChange,
  ...rest
}: Props) {
  const digits = value.replace(/[^0-9]/g, "").slice(-4);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      if (digits.length === 0) return;
      onValueChange(digits.slice(0, -1));
      return;
    }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const next = (digits + e.key).slice(-4);
      if (!isValid(next)) return;
      onValueChange(next);
      return;
    }
    const allow = [
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Enter",
      "Escape",
    ];
    if (!allow.includes(e.key) && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    const next = (digits + pasted).slice(-4);
    if (!isValid(next)) return;
    onValueChange(next);
  };

  return (
    <StyledInput
      type="text"
      inputMode="numeric"
      value={formatDisplay(digits)}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={() => {}}
      placeholder="00h:00m"
      {...rest}
    />
  );
}
